import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Sparkles, Clock, Home, ArrowRight, ChevronLeft,
  Shield, Award, Users, Loader2, Timer, Zap, Waves, Shirt
} from 'lucide-react';
import { format } from 'date-fns';
import axios from 'axios';

import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import useZipValidation from '../hooks/useZipValidation';

import { BookingProgress } from '../components/booking/BookingProgress';
import { ServiceStep } from '../components/booking/ServiceStep';
import { HomeStep } from '../components/booking/HomeStep';
import { ScheduleStep } from '../components/booking/ScheduleStep';
import { AddOnsStep } from '../components/booking/AddOnsStep';
import { CheckoutStep } from '../components/booking/CheckoutStep';
import { BookingSummary, fmtTime } from '../components/booking/BookingSummary';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/98287726-f61a-4612-9412-d077e45f3b87/images/72a48c594a6221af76e6b3f7e9afcd2161fa5ce3bc3473983f4b5f079cf8285c.png";

const SERVICES = [
  { id: 'maintenance', type: 'weekly', name: 'Maintenance Clean', desc: 'Weekly or biweekly upkeep for a consistently spotless home', time: '2–3 hrs', icon: Clock, baseMin: 120 },
  { id: 'deep_reset', type: 'deep_reset', name: 'Deep Reset', desc: 'Complete top-to-bottom deep cleaning transformation', time: '4–6 hrs', icon: Sparkles, badge: 'Required for new clients', deposit: true, baseMin: 270 },
  { id: 'move', type: 'deep_reset', name: 'Move-In / Move-Out', desc: 'Every surface, cabinet, and closet made immaculate', time: '5–7 hrs', icon: Home, deposit: true, baseMin: 330 },
  { id: 'carpet', type: 'carpet_cleaning', name: 'Carpet Cleaning', desc: 'Professional deep extraction for carpets and rugs', time: '1–3 hrs', icon: Waves, baseMin: 80 },
];

const ADDON_LIST = [
  { id: 'inside_fridge', name: 'Inside Fridge Clean', price: 35, icon: Sparkles },
  { id: 'inside_oven', name: 'Inside Oven Clean', price: 35, icon: Zap },
  { id: 'heavy_pet_hair', name: 'Pet Hair Treatment', price: 40, icon: Sparkles },
  { id: 'laundry_folding', name: 'Laundry & Folding', price: 40, icon: Shirt },
  { id: 'carpet_cleaning', name: 'Carpet Cleaning', price: 50, icon: Waves, hiddenForCarpet: true },
];

function mapHomeSize(beds, baths) {
  const b = parseInt(beds) || 3, bt = parseInt(baths) || 2;
  if (b >= 5) return '5+';
  const valid = ['1/1', '2/1', '2/2', '3/2', '4/3'];
  const exact = `${b}/${bt}`;
  if (valid.includes(exact)) return exact;
  const byBed = valid.filter(k => k.startsWith(`${b}/`));
  if (byBed.length) return byBed[byBed.length - 1];
  if (b >= 4) return '4/3';
  if (b >= 3) return '3/2';
  return bt >= 2 ? '2/2' : '2/1';
}

const BookingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isExistingClient } = useAuth();

  const [step, setStep] = useState(1);
  const preselect = searchParams.get('service');
  const [selectedService, setSelectedService] = useState(() =>
    SERVICES.find(s => s.type === preselect || s.id === preselect) || null
  );
  const [bedrooms, setBedrooms] = useState('3');
  const [bathrooms, setBathrooms] = useState('2');
  const [sqFt, setSqFt] = useState('1000_2000');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedWindow, setSelectedWindow] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [priceData, setPriceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showWaitlist, setShowWaitlist] = useState(false);

  const [carpetRooms, setCarpetRooms] = useState('2');
  const [carpetSqFt, setCarpetSqFt] = useState('1000_2000');
  const [stainSeverity, setStainSeverity] = useState('light');

  const [services, setServices] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);

  const { validateZip, validating, isServiceable, serviceAreaName, clearValidation } = useZipValidation();

  const [form, setForm] = useState({
    fullName: user?.full_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    serviceAddress: '',
    zipCode: '',
    hasPets: false,
    petNotes: '',
    parkingInstructions: '',
    accessInstructions: '',
  });

  const setField = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const serviceType = selectedService?.type || '';
  const isCarpet = serviceType === 'carpet_cleaning';
  const homeSize = mapHomeSize(bedrooms, bathrooms);
  const isRecurring = serviceType === 'weekly' || serviceType === 'biweekly';
  const showRecurringWarning = isRecurring && !isExistingClient;

  useEffect(() => {
    axios.get(`${API_URL}/api/services`).then(r => setServices(r.data)).catch(() => {});
  }, []);

  const getServiceId = useCallback(() => {
    const nameMap = { deep_reset: 'Signature Deep Reset', weekly: 'Weekly Maintenance', biweekly: 'Biweekly Maintenance', airbnb: 'Airbnb Turnover' };
    const svc = services.find(s => s.name === nameMap[serviceType]);
    return svc?.id;
  }, [serviceType, services]);

  useEffect(() => {
    if (!selectedDate || !serviceType) return;
    if (isCarpet) return;
    const svcId = getServiceId();
    if (!svcId) return;
    setSlotsLoading(true);
    axios
      .get(`${API_URL}/api/schedule/available-slots`, { params: { date: format(selectedDate, 'yyyy-MM-dd'), service_id: svcId } })
      .then(() => {})
      .catch(() => {})
      .finally(() => setSlotsLoading(false));
  }, [selectedDate, serviceType, services, getServiceId, isCarpet]);

  useEffect(() => {
    const z = form.zipCode.trim();
    if (z.length === 5) { validateZip(z); }
    else { clearValidation(); setShowWaitlist(false); }
  }, [form.zipCode, validateZip, clearValidation]);

  useEffect(() => {
    if (!serviceType) { setPriceData(null); return; }
    const addonsPayload = selectedAddons.map(id => ({ addon_id: id, quantity: 1 }));
    const body = isCarpet
      ? { service_type: 'carpet_cleaning', home_size: '', carpet_rooms: parseInt(carpetRooms) || 2, stain_treatment: stainSeverity !== 'light', addons: addonsPayload, linen_sets: 0 }
      : { service_type: serviceType, home_size: homeSize, addons: addonsPayload, linen_sets: 0 };
    axios.post(`${API_URL}/api/pricing/calculate`, body)
      .then(r => setPriceData(r.data))
      .catch(() => {});
  }, [serviceType, homeSize, selectedAddons, isCarpet, carpetRooms, stainSeverity]);

  const estimatedMinutes = useMemo(() => {
    if (priceData?.duration_minutes) return priceData.duration_minutes;
    if (!selectedService) return 120;
    if (isCarpet) return (parseInt(carpetRooms) || 2) * 40;
    const extra = ((parseInt(bedrooms) || 3) - 2) * 20 + ((parseInt(bathrooms) || 2) - 1) * 15;
    return selectedService.baseMin + Math.max(0, extra);
  }, [priceData, selectedService, bedrooms, bathrooms, isCarpet, carpetRooms]);

  const disabledDates = useCallback((date) => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return date < today || date.getDay() === 0;
  }, []);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const addonsPayload = selectedAddons.map(id => ({ addon_id: id, quantity: 1 }));
      const dateStr = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : '';
      const arrivalStart = selectedWindow ? `${dateStr}T${selectedWindow.start}:00` : '';
      const arrivalEnd = selectedWindow ? `${dateStr}T${selectedWindow.end}:00` : '';

      const bookingData = {
        service_type: serviceType,
        home_size: isCarpet ? '' : homeSize,
        addons: addonsPayload,
        linen_sets: 0,
        full_name: form.fullName,
        email: form.email,
        phone: form.phone,
        service_address: form.serviceAddress,
        zip_code: form.zipCode,
        has_pets: form.hasPets,
        pet_notes: form.petNotes,
        parking_instructions: form.parkingInstructions,
        access_instructions: form.accessInstructions,
        preferred_date: dateStr,
        preferred_time: selectedWindow?.label || '',
        arrival_start_datetime: arrivalStart,
        arrival_end_datetime: arrivalEnd,
        estimated_minutes: estimatedMinutes,
        carpet_rooms: isCarpet ? parseInt(carpetRooms) || 2 : 0,
        stain_treatment: isCarpet && stainSeverity !== 'light',
      };

      const response = await axios.post(`${API_URL}/api/bookings`, bookingData);

      try {
        const checkoutResponse = await axios.post(`${API_URL}/api/payments/checkout`, {
          booking_id: response.data.id,
          origin_url: window.location.origin,
        });
        window.location.href = checkoutResponse.data.checkout_url;
      } catch (checkoutError) {
        if (checkoutError.response?.status === 503) {
          navigate('/booking/success?confirmed=manual');
        } else {
          throw checkoutError;
        }
      }
    } catch (error) {
      if (error.response?.status === 202) {
        navigate('/booking/estimate-submitted');
      } else {
        alert(error.response?.data?.detail || 'Booking failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const canProceed = useMemo(() => {
    switch (step) {
      case 1: return selectedService && !showRecurringWarning;
      case 2: return isCarpet ? carpetRooms : (bedrooms && bathrooms && sqFt);
      case 3: return selectedDate && selectedWindow;
      case 4: return true;
      case 5:
        return form.fullName && form.email && form.phone &&
          form.serviceAddress && form.zipCode && isServiceable === true;
      default: return false;
    }
  }, [step, selectedService, bedrooms, bathrooms, sqFt, selectedDate, selectedWindow, form, showRecurringWarning, isServiceable, isCarpet, carpetRooms]);

  const next = () => { if (canProceed && step < 5) setStep(step + 1); };
  const back = () => { if (step > 1) setStep(step - 1); };

  const visibleAddons = ADDON_LIST.filter(a => !(isCarpet && a.hiddenForCarpet));

  return (
    <div className="min-h-screen pt-24 bg-zinc-50" data-testid="booking-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">

        {/* Trust Row */}
        <div className="flex flex-wrap justify-center gap-6 mb-6" data-testid="booking-trust-row">
          {[
            { icon: Users, text: 'Trusted by Portland homeowners' },
            { icon: Shield, text: 'Background-checked cleaners' },
            { icon: Award, text: 'Satisfaction guarantee' },
          ].map((t) => (
            <div key={t.text} className="flex items-center gap-2 text-sm text-zinc-500">
              <t.icon className="h-4 w-4 text-[#F56F05]" /><span>{t.text}</span>
            </div>
          ))}
        </div>

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-6">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-zinc-900 font-heading mb-2" data-testid="booking-title">
            Book Your Clean
          </h1>
          <p className="text-sm text-zinc-500 flex items-center justify-center gap-1.5">
            <Timer className="h-4 w-4" /> Booking takes less than 60 seconds.
          </p>
        </motion.div>

        <BookingProgress step={step} />

        {/* Split Layout */}
        <div className="grid lg:grid-cols-5 gap-8">

          {/* Hero Image */}
          <div className="hidden lg:block lg:col-span-2">
            <div className="sticky top-28 rounded-2xl overflow-hidden shadow-xl border border-zinc-200">
              <img src={HERO_IMG} alt="Luxury clean home" className="w-full h-[520px] object-cover" data-testid="booking-hero-image" />
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                <p className="text-white font-bold text-lg">Gleam Guru</p>
                <p className="text-white/70 text-sm">Portland&apos;s premium cleaning service</p>
              </div>
            </div>
          </div>

          {/* Booking Flow + Summary */}
          <div className="lg:col-span-3">
            <div className="grid gap-6 md:grid-cols-[1fr_280px]">

              {/* Steps */}
              <div className="min-w-0">
                <AnimatePresence mode="wait">
                  {step === 1 && (
                    <ServiceStep
                      services={SERVICES}
                      selectedService={selectedService}
                      setSelectedService={setSelectedService}
                      isExistingClient={isExistingClient}
                      showRecurringWarning={showRecurringWarning}
                    />
                  )}
                  {step === 2 && (
                    <HomeStep
                      isCarpet={isCarpet}
                      bedrooms={bedrooms}
                      setBedrooms={setBedrooms}
                      bathrooms={bathrooms}
                      setBathrooms={setBathrooms}
                      sqFt={sqFt}
                      setSqFt={setSqFt}
                      carpetRooms={carpetRooms}
                      setCarpetRooms={setCarpetRooms}
                      carpetSqFt={carpetSqFt}
                      setCarpetSqFt={setCarpetSqFt}
                      stainSeverity={stainSeverity}
                      setStainSeverity={setStainSeverity}
                    />
                  )}
                  {step === 3 && (
                    <ScheduleStep
                      selectedDate={selectedDate}
                      setSelectedDate={setSelectedDate}
                      selectedWindow={selectedWindow}
                      setSelectedWindow={setSelectedWindow}
                      disabledDates={disabledDates}
                      slotsLoading={slotsLoading}
                    />
                  )}
                  {step === 4 && (
                    <AddOnsStep
                      visibleAddons={visibleAddons}
                      selectedAddons={selectedAddons}
                      setSelectedAddons={setSelectedAddons}
                    />
                  )}
                  {step === 5 && (
                    <CheckoutStep
                      form={form}
                      setField={setField}
                      validating={validating}
                      isServiceable={isServiceable}
                      serviceAreaName={serviceAreaName}
                      showWaitlist={showWaitlist}
                      setShowWaitlist={setShowWaitlist}
                    />
                  )}
                </AnimatePresence>

                {/* Navigation Buttons */}
                <div className="flex items-center justify-between mt-5">
                  <Button variant="ghost" onClick={back} disabled={step === 1} className="text-zinc-600 hover:text-zinc-900" data-testid="btn-back">
                    <ChevronLeft className="h-4 w-4 mr-1" /> Back
                  </Button>
                  {step < 5 ? (
                    <Button onClick={next} disabled={!canProceed}
                      className="bg-[#F56F05] hover:bg-[#C2410C] text-white font-bold px-8 h-11 rounded-xl shadow-lg shadow-orange-200/50 disabled:opacity-40"
                      data-testid="btn-next">
                      Continue <ArrowRight className="h-4 w-4 ml-1" />
                    </Button>
                  ) : (
                    <Button onClick={handleSubmit} disabled={!canProceed || loading}
                      className="bg-[#F56F05] hover:bg-[#C2410C] text-white font-bold px-8 h-12 rounded-xl shadow-lg shadow-orange-200/50 disabled:opacity-40"
                      data-testid="btn-confirm">
                      {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Processing...</> : <>Confirm My Booking <ArrowRight className="h-4 w-4 ml-1" /></>}
                    </Button>
                  )}
                </div>
              </div>

              {/* Summary */}
              <div className="hidden sm:block">
                <BookingSummary
                  step={step}
                  selectedService={selectedService}
                  bedrooms={bedrooms}
                  bathrooms={bathrooms}
                  isCarpet={isCarpet}
                  carpetRooms={carpetRooms}
                  stainSeverity={stainSeverity}
                  selectedDate={selectedDate}
                  selectedWindow={selectedWindow}
                  selectedAddons={selectedAddons}
                  estimatedMinutes={estimatedMinutes}
                  priceData={priceData}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Summary */}
        <div className="sm:hidden mt-6">
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-zinc-100" data-testid="booking-summary-mobile">
            <h3 className="text-base font-bold text-zinc-900 font-heading mb-3">Summary</h3>
            <div className="flex items-center justify-between text-sm">
              <span className="text-zinc-500">{selectedService?.name || 'No service selected'}</span>
              {priceData && !priceData.requires_estimate && (
                <span className="text-lg font-bold text-[#F56F05]">${priceData.total_price.toFixed(2)}</span>
              )}
            </div>
            {estimatedMinutes > 0 && selectedService && (
              <p className="text-xs text-zinc-400 mt-1">Est. {fmtTime(estimatedMinutes)}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingPage;
